<script setup>
  import { useCartTransferStore } from '../../stores/CartTransferStore';
  import { onMounted, computed, ref } from 'vue';
  import Loader from '../../components/ui/Loader.vue';
  import Button from '../../components/ui/Button.vue';
  import { useI18n } from 'vue-i18n';

  const transferStore = useCartTransferStore();
  const { t } = useI18n();
  const emits = defineEmits(['close']);
  const props = defineProps({
    cartId: {
      type: Object,
      required: true,
    },
  });

  onMounted(async () => {
    loading.value.libraries = true;
    await transferStore.fetchLibraries(props.cartId);
    setLibrary(transferStore.currrentLibrary);
    loading.value.libraries = false;
  });

  const loading = ref({ libraries: false, libraryUsers: false });
  const selectedItem = ref({ library: { id: null, name: null }, user: { id: null, name: null } });

  const libraries = computed(() => {
    return transferStore.libraries;
  });

  const users = computed(() => {
    if(transferStore.libraryUsers) {
      return transferStore.libraryUsers;
    }
    return [];
  });

  const onLibrarySelect = (e) => {
    const library = libraries.value.find(l => l.id == e.target.value);
    if (library) setLibrary(library);
  };

  const setLibrary = (library) => {
    formData.value.library_id = library.id;
    selectedItem.value.library = library;
    selectedItem.value.user = { id: null, name: null };
    findOrFetchLibraryUsers(library.id);
  };

  const onUserSelect = (e) => {
    const user = users.value.find(u => u.id == e.target.value);
    if (user) setUser(user);
  };

  const setUser = (user) => {
    formData.value.user_id = user.id;
    selectedItem.value.user = user;
  };

  const findOrFetchLibraryUsers = async (libraryId) => {
    loading.value.libraryUsers = true;
    formData.value.user_id = '';
    await transferStore.fetchLibraryUsers(libraryId);
    const users = transferStore.libraryUsers
    if (users.length > 0) { setUser(users[0]) }
    
    loading.value.libraryUsers = false;
  };

  const formData = ref({
    user_id: '',
    library_id: ''
  });

  const sendForm = async () => {
    const params = { cart: formData.value };
    params.cart.cart_transfer = true;
    if(!confirm(t('cart.move_cart.confirm_save'))) { return; }
    const response = await transferStore.transferCart(props.cartId, params);
    if (!transferStore.errors) {
      window.location.href = response.url;
    }
  };

  const validateForm = computed(() => {
    return formData.value.library_id !== '' && formData.value.user_id !== '';
  });
</script>

<template>
  <div class="error-container" v-if="transferStore.errors">
    <div class="error-container--title">
      {{ transferStore.errors.title || t('cart.move_cart.generic_error.title') }}
    </div>
    <p>
      {{ transferStore.errors.details || t('cart.move_cart.generic_error.details') }}
    </p>
  </div>
  <Loader v-if="loading.libraries" />
  <div v-else>
    <div class="form-row">
      <div class="form-field-container">
        <select class="form-select" :value="formData.library_id" @change="onLibrarySelect">
          <option v-for="lib in libraries" :key="lib.id" :value="lib.id">{{ lib.name }}</option>
        </select>
      </div>
    </div>
    <template v-if="loading.libraryUsers">
      <Loader />
    </template>
    <template v-if="!loading.libraryUsers">
      <div class="form-row" v-if="users.length > 0">
        <div class="form-field-container">
          <select class="form-select" :value="formData.user_id" @change="onUserSelect">
            <option v-for="user in users" :key="user.id" :value="user.id">{{ user.name || user.email }}</option>
          </select>
        </div>
      </div>
      <p v-else>
        {{ t('cart.move_cart.manage_users.description') }}
        <a :href="`/admin/libraries/${formData.library_id}/user_roles`">
          {{ t('cart.move_cart.manage_users.title') }}
        </a>
      </p>
    </template>
    <div class="button-bar">
      <Button type_button="submit" class="btn btn-primary" @click.prevent="sendForm" :disabled="!validateForm">
        {{ t('cart.form.save') }}
      </Button>
      <Button type_button="button" class="btn btn-outline-secondary" @click.prevent="emits('close')">
        {{ t('cart.form.cancel') }}
      </Button>
    </div>
  </div>
</template>

<style scoped>
 .button-bar {
  display: flex;
  justify-content: center;
  margin-top: 20px;
 }

</style>
